
const TESTS_API_BASE = '/api/tests/';
const EXAMS_API_BASE = '/api/exams/';
const EXAM_QUESTIONS_API_BASE = '/api/exam-questions/';
const TEST_QUESTIONS_API_BASE = '/api/test-questions/';
const EXAM_CHOICES_API_BASE = '/api/exam-choices/';
const TEST_CHOICES_API_BASE = '/api/test-choices/';

function authHeaders() {
	const token = localStorage.getItem('authToken');
	return token ? { 'Authorization': `Token ${token}` } : {};
}

async function readErrorMessage(response) {
	try {
		const data = await response.json();
		if (data && typeof data === 'object') {
			return Object.entries(data)
				.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
				.join(' | ');
		}
		return JSON.stringify(data);
	} catch {
		return await response.text();
	}
}

// --- Test API ---

// Exams and Tests

export async function fetchTests() {
	const response = await fetch(TESTS_API_BASE, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch tests');
	return await response.json();
}

// Get tests for a single level (server-side filtered, spans every topic in the level)
export async function fetchTestsByLevel(levelId) {
	const response = await fetch(`${TESTS_API_BASE}?level=${levelId}`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch tests');
	return await response.json();
}

export async function fetchTest(id) {
	const response = await fetch(`${TESTS_API_BASE}${id}/`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test');
	return await response.json();
}

export async function createTest(data) {
		const token = localStorage.getItem('authToken');
		const response = await fetch(TESTS_API_BASE, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(token ? { 'Authorization': `Token ${token}` } : {})
			},
			body: JSON.stringify(data),
		});
		if (!response.ok) throw new Error('Failed to create test');
		return await response.json();
}

export async function updateTest(id, data) {
		const token = localStorage.getItem('authToken');
		const response = await fetch(`${TESTS_API_BASE}${id}/`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				...(token ? { 'Authorization': `Token ${token}` } : {})
			},
			body: JSON.stringify(data),
		});
		if (!response.ok) throw new Error('Failed to update test');
		return await response.json();
}

export async function deleteTest(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${TESTS_API_BASE}${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true; // treat as success if already deleted
	if (!response.ok) throw new Error('Failed to delete test');
	return true;
}

// --- Exam API ---
export async function fetchExams() {
	const response = await fetch(EXAMS_API_BASE, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch final exams');
	return await response.json();
}

// Get final exams for a single level (server-side filtered)
export async function fetchExamsByLevel(levelId) {
	const response = await fetch(`${EXAMS_API_BASE}?level=${levelId}`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch final exams');
	return await response.json();
}

export async function fetchExam(id) {
	const response = await fetch(`${EXAMS_API_BASE}${id}/`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch final exam');
	return await response.json();
}

export async function createExam(data) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(EXAMS_API_BASE, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Token ${token}` } : {})
        },
        body: JSON.stringify(data),
    });
	if (!response.ok) throw new Error('Failed to create exam');
    return await response.json();
}

export async function updateExam(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${EXAMS_API_BASE}${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update exam');
	return await response.json();
}

export async function deleteExam(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${EXAMS_API_BASE}${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true; // treat as success if already deleted
	if (!response.ok) throw new Error('Failed to delete exam');
	return true;
}

// --- Exam Question API ---
export async function fetchExamQuestions() {
	const response = await fetch(EXAM_QUESTIONS_API_BASE, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch exam questions');
	return await response.json();
}

// Get questions for a single exam (server-side filtered)
export async function fetchExamQuestionsByExam(examId) {
	const response = await fetch(`${EXAM_QUESTIONS_API_BASE}?exam=${examId}`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch exam questions');
	return await response.json();
}

export async function fetchExamQuestion(id) {
	const response = await fetch(`${EXAM_QUESTIONS_API_BASE}${id}/`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch exam question');
	return await response.json();
}

export async function createExamQuestion(data) {
	const token = localStorage.getItem('authToken');
	const hasFile = Object.values(data || {}).some(
		value => value instanceof File || value instanceof Blob
	);
	const isFormData = data instanceof FormData;
	let body = data;
	if (!isFormData && hasFile) {
		body = new FormData();
		Object.entries(data).forEach(([key, value]) => {
			if (value === undefined) return;
			body.append(key, value === null ? '' : value);
		});
	}
	const useJson = !isFormData && !hasFile;
	const response = await fetch(EXAM_QUESTIONS_API_BASE, {
		method: 'POST',
		headers: {
			...(useJson ? { 'Content-Type': 'application/json' } : {}),
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: useJson ? JSON.stringify(data) : body,
	});
	if (!response.ok) {
		throw new Error(`Failed to create exam question: ${await readErrorMessage(response)}`);
	}
	return await response.json();
}

export async function updateExamQuestion(id, data) {
	const token = localStorage.getItem('authToken');
	const hasFile = Object.values(data || {}).some(
		value => value instanceof File || value instanceof Blob
	);
	const isFormData = data instanceof FormData;
	let body = data;
	if (!isFormData && hasFile) {
		body = new FormData();
		Object.entries(data).forEach(([key, value]) => {
			if (value === undefined) return;
			body.append(key, value === null ? '' : value);
		});
	}
	const useJson = !isFormData && !hasFile;
	const response = await fetch(`${EXAM_QUESTIONS_API_BASE}${id}/`, {
		method: 'PUT',
		headers: {
			...(useJson ? { 'Content-Type': 'application/json' } : {}),
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: useJson ? JSON.stringify(data) : body,
	});
	if (!response.ok) {
		throw new Error(`Failed to update exam question: ${await readErrorMessage(response)}`);
	}
	return await response.json();
}

export async function deleteExamQuestion(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${EXAM_QUESTIONS_API_BASE}${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true;
	if (!response.ok) throw new Error('Failed to delete exam question');
	return true;
}

// --- Test Question API ---
export async function fetchTestQuestions() {
	const response = await fetch(TEST_QUESTIONS_API_BASE, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test questions');
	return await response.json();
}

// Get questions for a single test (server-side filtered)
export async function fetchTestQuestionsByTest(testId) {
	const response = await fetch(`${TEST_QUESTIONS_API_BASE}?test=${testId}`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test questions');
	return await response.json();
}

export async function fetchTestQuestion(id) {
	const response = await fetch(`${TEST_QUESTIONS_API_BASE}${id}/`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test question');
	return await response.json();
}

export async function createTestQuestion(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(TEST_QUESTIONS_API_BASE, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create test question');
	return await response.json();
}

export async function updateTestQuestion(id, data) {
	const token = localStorage.getItem('authToken');
	const hasFile = Object.values(data || {}).some(
		value => value instanceof File || value instanceof Blob
	);
	const isFormData = data instanceof FormData;
	let body = data;
	if (!isFormData && hasFile) {
		body = new FormData();
		Object.entries(data).forEach(([key, value]) => {
			if (value === undefined) return;
			body.append(key, value === null ? '' : value);
		});
	}
	const useJson = !isFormData && !hasFile;
	const response = await fetch(`${TEST_QUESTIONS_API_BASE}${id}/`, {
		method: 'PUT',
		headers: {
			...(useJson ? { 'Content-Type': 'application/json' } : {}),
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: useJson ? JSON.stringify(data) : body,
	});
	if (!response.ok) {
		throw new Error(`Failed to update test question: ${await readErrorMessage(response)}`);
	}
	return await response.json();
}

export async function deleteTestQuestion(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${TEST_QUESTIONS_API_BASE}${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true;
	if (!response.ok) throw new Error('Failed to delete test question');
	return true;
}

// --- Exam Choice API ---
export async function fetchExamChoices() {
	const response = await fetch(EXAM_CHOICES_API_BASE, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch exam choices');
	return await response.json();
}

// Get choices belonging to any question in a single exam (server-side filtered)
export async function fetchExamChoicesByExam(examId) {
	const response = await fetch(`${EXAM_CHOICES_API_BASE}?exam=${examId}`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch exam choices');
	return await response.json();
}

export async function fetchExamChoice(id) {
	const response = await fetch(`${EXAM_CHOICES_API_BASE}${id}/`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch exam choice');
	return await response.json();
}

export async function createExamChoice(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(EXAM_CHOICES_API_BASE, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create exam choice');
	return await response.json();
}

export async function updateExamChoice(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${EXAM_CHOICES_API_BASE}${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update exam choice');
	return await response.json();
}

export async function deleteExamChoice(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${EXAM_CHOICES_API_BASE}${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true;
	if (!response.ok) throw new Error('Failed to delete exam choice');
	return true;
}

// --- Test Choice API ---
export async function fetchTestChoices() {
	const response = await fetch(TEST_CHOICES_API_BASE, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test choices');
	return await response.json();
}

// Get choices belonging to any question in a single test (server-side filtered)
export async function fetchTestChoicesByTest(testId) {
	const response = await fetch(`${TEST_CHOICES_API_BASE}?test=${testId}`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test choices');
	return await response.json();
}

export async function fetchTestChoice(id) {
	const response = await fetch(`${TEST_CHOICES_API_BASE}${id}/`, {
		headers: authHeaders()
	});
	if (!response.ok) throw new Error('Failed to fetch test choice');
	return await response.json();
}

export async function createTestChoice(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(TEST_CHOICES_API_BASE, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create test choice');
	return await response.json();
}

export async function updateTestChoice(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${TEST_CHOICES_API_BASE}${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update test choice');
	return await response.json();
}

export async function deleteTestChoice(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`${TEST_CHOICES_API_BASE}${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true;
	if (!response.ok) throw new Error('Failed to delete test choice');
	return true;
}


// --- User Exam Attempt API ---
export async function fetchUserExamAttempts() {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-exam-attempts/', {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user exam attempts');
	return await response.json();
}

// Get the current user's attempts for a single exam (server-side filtered)
export async function fetchUserExamAttemptsByExam(examId) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-exam-attempts/?exam=${examId}`, {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user exam attempts');
	return await response.json();
}

export async function createUserExamAttempt(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-exam-attempts/', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create user exam attempt');
	return await response.json();
}

export async function updateUserExamAttempt(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-exam-attempts/${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update user exam attempt');
	return await response.json();
}

// Fire-and-forget variant safe to call while the page is unloading: `keepalive`
// lets the browser deliver the request even after the tab closes.
export function updateUserExamAttemptKeepalive(id, data) {
	const token = localStorage.getItem('authToken');
	return fetch(`/api/user-exam-attempts/${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
		keepalive: true,
	}).catch(() => {});
}


// --- User Test Attempt API ---
export async function fetchUserTestAttempts() {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-test-attempts/', {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user test attempts');
	return await response.json();
}

// Get the current user's attempts for a single test (server-side filtered)
export async function fetchUserTestAttemptsByTest(testId) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-test-attempts/?test=${testId}`, {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user test attempts');
	return await response.json();
}

export async function createUserTestAttempt(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-test-attempts/', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create user test attempt');
	return await response.json();
}

export async function updateUserTestAttempt(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-test-attempts/${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update user test attempt');
	return await response.json();
}

// Fire-and-forget variant safe to call while the page is unloading: `keepalive`
// lets the browser deliver the request even after the tab closes.
export function updateUserTestAttemptKeepalive(id, data) {
	const token = localStorage.getItem('authToken');
	return fetch(`/api/user-test-attempts/${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
		keepalive: true,
	}).catch(() => {});
}


// --- User Exam Question Choice API ---
export async function fetchUserExamQuestionChoices() {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-exam-question-choices/', {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user exam question choices');
	return await response.json();
}

// Get the current user's selected choices for a single exam (server-side filtered)
export async function fetchUserExamQuestionChoicesByExam(examId) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-exam-question-choices/?exam=${examId}`, {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user exam question choices');
	return await response.json();
}

export async function createUserExamQuestionChoice(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-exam-question-choices/', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create user exam question choice');
	return await response.json();
}

export async function updateUserExamQuestionChoice(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-exam-question-choices/${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update user exam question choice');
	return await response.json();
}

export async function deleteUserExamQuestionChoice(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-exam-question-choices/${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true;
	if (!response.ok) throw new Error('Failed to delete user exam question choice');
	return true;
}


// --- User Test Question Choice API ---
export async function fetchUserTestQuestionChoices() {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-test-question-choices/', {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user test question choices');
	return await response.json();
}

// Get the current user's selected choices for a single test (server-side filtered)
export async function fetchUserTestQuestionChoicesByTest(testId) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-test-question-choices/?test=${testId}`, {
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (!response.ok) throw new Error('Failed to fetch user test question choices');
	return await response.json();
}

export async function createUserTestQuestionChoice(data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch('/api/user-test-question-choices/', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to create user test question choice');
	return await response.json();
}

export async function updateUserTestQuestionChoice(id, data) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-test-question-choices/${id}/`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': `Token ${token}` } : {})
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) throw new Error('Failed to update user test question choice');
	return await response.json();
}

export async function deleteUserTestQuestionChoice(id) {
	const token = localStorage.getItem('authToken');
	const response = await fetch(`/api/user-test-question-choices/${id}/`, {
		method: 'DELETE',
		headers: {
			...(token ? { 'Authorization': `Token ${token}` } : {})
		}
	});
	if (response.status === 404) return true;
	if (!response.ok) throw new Error('Failed to delete user test question choice');
	return true;
}
